import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

/**
 * Our Commitment — final closing brand statement on matte-black bg.
 * Used on LMTB as Section 15. Optional per product.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Our Commitment")
 *  - heading (ReactNode)
 *  - paragraphs: string[]
 *  - badges: string[]
 */
export default function OurCommitment({
  testId,
  eyebrow = "Our Commitment",
  heading,
  paragraphs = [],
  badges = [],
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-matte-black py-28 md:py-40 lg:py-44 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px bg-liftile-orange/30"
      />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] rounded-full pointer-events-none opacity-[0.10]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="heading-eyebrow mb-5">{eyebrow}</div>
          <div className="mx-auto w-14 h-px bg-liftile-orange mb-9" />

          <h2
            data-testid={`${testId}-heading`}
            className="font-display uppercase text-liftile-cream leading-[1.02] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[3.75rem]"
          >
            {heading}
          </h2>

          <div className="mt-11 md:mt-12 space-y-6 md:space-y-7 font-body text-base md:text-lg lg:text-[1.15rem] text-liftile-cream/72 leading-[1.85] max-w-3xl mx-auto">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {badges.length > 0 && (
            <motion.ul
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.25, duration: 0.7 }}
              data-testid={`${testId}-badges`}
              className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
            >
              {badges.map((b) => (
                <li
                  key={b}
                  className="inline-flex items-center gap-2 font-heading text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-liftile-cream/80 font-semibold"
                >
                  <Check
                    className="w-3.5 h-3.5 text-liftile-orange"
                    strokeWidth={3}
                  />
                  {b}
                </li>
              ))}
            </motion.ul>
          )}
        </motion.div>
      </div>
    </section>
  );
}
